import { SearchInput } from 'components/searchInput/searchInput'
import { useProductSearch } from 'helpers'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { Box, Hidden } from 'ui/core'
import { HeaderButton } from '../headerControls/headerControls'
import { Dimmer } from './headerSearchInput.styles'
import { SearchDropdown } from './searchDropdown/searchDropdown'
import { SearchModal } from './searchModal/searchModal'
import { HeaderToggleContext } from 'helpers/contexts/headerToggleContext'

export const HeaderSearchInput: FC = () => {
  const debounce = 300
  const ref = useRef<HTMLInputElement | null>(null)
  const [query, setQuery] = useState('')
  const { results, status, value, setValue, postSearchResult } =
    useProductSearch()
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)

  const toggleContext = useContext(HeaderToggleContext)

  // Debounced search value by default
  useDebounce(
    () => {
      if (query !== value) setValue(query)
    },
    debounce,
    [query],
  )

  const setInputFocus = () => {
    if (ref && ref.current) {
      ref.current.focus()
    }
  }

  useEffect(() => {
    if (mobileSearchOpen && toggleContext) {
      toggleContext.onMobileSearchOpen();
    }
  }, [mobileSearchOpen, toggleContext])

  return (
    <>
      <Box
        display="flex"
        justifyContent={['flex-start', 'flex-end', 'space-between']}
        alignItems="center"
        height="full"
        position="relative"
      >
        <Dimmer
          width="viewportWidth"
          height="viewportHeight"
          position="fixed"
          bg="dark400"
          isSearching={status === 'fulfilled' || status === 'loading'}
          zIndex="-1"
          display={['none', 'none', 'none', 'inherit']}
        />
        <Hidden below="lg" height="full" width="full">
          <Box
            width={['full', 'full', 'full', 'full', '8/12', '7/12']}
            position="relative"
            style={{ top: 5 }}
            pl={1}
          >
            <SearchInput
              noFocusBorder
              ref={ref}
              autoComplete="off"
              placeholder="Leita..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (document.body.dataset.store === 'Iskraft') {
                    window.location.href = document.body.dataset.rootUrl + 'leit?q=' + query
                  }
                  else {
                    window.location.href = '/leit?q=' + query
                  }
                }
              }}
            />
          </Box>
          <Box
            height="full"
            onClick={() => setInputFocus()}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={[1, 2, 4]}
            borderRight="light"
          >
            <HeaderButton
              iconLeft="search"
              iconColor="primary300"
              hiddenBelow="md"
            />
          </Box>
        </Hidden>
        <Hidden above="lg" px={[1, 2]}>
          <SearchModal
            query={query}
            loading={status === 'loading'}
            isSearching={mobileSearchOpen}
            setIsSearching={setMobileSearchOpen}
            setQuery={setQuery}
            productResults={results || null}
            productsLoading={status === 'loading'}
            postSearchResult={postSearchResult}
            total={
              (results?.Count || 0) +
              (results?.ProductCategories?.length || 0) +
              (results?.Content?.length || 0)
            }
          />
        </Hidden>
      </Box>
      <Box
        right={[10, 10, 10, 20, 'auto']}
        position="relative"
        width={['full', 'full', 'full', 'full', '8/12', '7/12']}
      >
        <SearchDropdown
          query={query}
          closeSearch={() => !mobileSearchOpen && setQuery('')}
          searching={status === 'fulfilled' || status === 'loading'}
          productResults={results || null}
          productsLoading={status === 'loading'}
          postSearchResult={postSearchResult}
          total={
            (results?.Count || 0) +
            (results?.ProductCategories?.length || 0) +
            (results?.Content?.length || 0)
          }
        />
      </Box>
    </>
  )
}
